import { useState, useEffect } from 'react';
import { toast } from './Toast';
import api from '../api/index';
import { ENDPOINTS } from '../api/endpoints';
import { findKenyaCountry } from '../utils/locations';
import {
    formatPhoneWithPrefix,
    isValidKenyanPhone,
    normalizeKenyanPhone,
    KENYA_PHONE_ERROR,
    KENYA_PHONE_HINT,
    KENYA_PHONE_PLACEHOLDER,
} from '../utils/phone';

// Validation helper functions
const validateName = (name) => {
    // Only letters, spaces, hyphens, and apostrophes
    return /^[a-zA-Z\s'-]+$/.test(name);
};

const validatePostalCode = (code) => {
    // African postal codes: typically 3-10 alphanumeric characters
    // Kenya: 5 digits (e.g., 00100)
    // South Africa: 4 digits
    // Nigeria: 6 digits
    // Ghana: alphanumeric
    return /^[A-Z0-9]{3,10}$/i.test(code);
};

export default function AddressForm({
    initialData,
    onSubmit,
    onCancel,
    isLoading,
    submitLabel = 'Save Address',
    contactPrefill = null,
    showUseSameAsAbove = false,
}) {
    const [form, setForm] = useState({
        fullName: initialData?.fullName || '',
        phone: initialData?.phone
            ? formatPhoneWithPrefix(normalizeKenyanPhone(initialData.phone))
            : '+254',
        addressLine1: initialData?.addressLine1 || '',
        addressLine2: initialData?.addressLine2 || '',
        country: initialData?.country || '',
        state: initialData?.state || '',
        city: initialData?.city || '',
        postalCode: initialData?.postalCode || '',
        label: (initialData?.label || 'home').toString().toLowerCase(),
    });

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);
    const [errors, setErrors] = useState({});

    // Initialize phone with +254 prefix if empty
    useEffect(() => {
        if (!initialData?.phone && (!form.phone || form.phone === '')) {
            setForm(prev => ({ ...prev, phone: '+254' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch countries on mount
    useEffect(() => {
        const fetchCountries = async () => {
            setLoadingCountries(true);
            try {
                const res = await api.get(ENDPOINTS.LOCATIONS.COUNTRIES);
                if (res.data?.isOk && res.data?.data) {
                    const list = res.data.data;
                    setCountries(list);
                    if (!initialData?.country) {
                        const kenya = findKenyaCountry(list);
                        if (kenya) {
                            setForm((prev) => (prev.country ? prev : { ...prev, country: kenya._id }));
                        }
                    }
                }
            } catch (err) {
                console.error('Error fetching countries:', err);
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountries();
    }, []);

    // Fetch states when country changes
    useEffect(() => {
        if (!form.country) {
            setStates([]);
            setCities([]);
            return;
        }
        const fetchStates = async () => {
            setLoadingStates(true);
            setStates([]);
            setCities([]);
            setForm(prev => ({ ...prev, state: '', city: '' }));
            try {
                const res = await api.get(ENDPOINTS.LOCATIONS.STATES(form.country));
                if (res.data?.isOk && res.data?.data) {
                    setStates(res.data.data);
                }
            } catch (err) {
                console.error('Error fetching states:', err);
            } finally {
                setLoadingStates(false);
            }
        };
        fetchStates();
    }, [form.country]);

    // Fetch cities when state changes
    useEffect(() => {
        if (!form.state) {
            setCities([]);
            return;
        }
        const fetchCities = async () => {
            setLoadingCities(true);
            setCities([]);
            setForm(prev => ({ ...prev, city: '' }));
            try {
                const res = await api.get(ENDPOINTS.LOCATIONS.CITIES(form.state));
                if (res.data?.isOk && res.data?.data) {
                    setCities(res.data.data);
                }
            } catch (err) {
                console.error('Error fetching cities:', err);
            } finally {
                setLoadingCities(false);
            }
        };
        fetchCities();
    }, [form.state]);

    // Pre-load states and cities for editing
    useEffect(() => {
        if (initialData?.country && countries.length > 0) {
            const loadEditData = async () => {
                try {
                    if (initialData.country) {
                        const statesRes = await api.get(ENDPOINTS.LOCATIONS.STATES(initialData.country));
                        if (statesRes.data?.isOk && statesRes.data?.data) {
                            setStates(statesRes.data.data);
                        }
                    }
                    if (initialData.state) {
                        const citiesRes = await api.get(ENDPOINTS.LOCATIONS.CITIES(initialData.state));
                        if (citiesRes.data?.isOk && citiesRes.data?.data) {
                            setCities(citiesRes.data.data);
                        }
                    }
                } catch (err) {
                    console.error('Error loading edit data:', err);
                }
            };
            loadEditData();
        }
    }, [initialData, countries]);

    const applySameAsAbove = () => {
        const fullName = String(contactPrefill?.fullName || '').trim();
        const rawPhone = String(contactPrefill?.phone || '').trim();
        if (!fullName && (!rawPhone || rawPhone === '+254')) {
            toast.error('Fill in your details above first');
            return;
        }
        setForm((prev) => ({
            ...prev,
            fullName: fullName || prev.fullName,
            phone: rawPhone
                ? formatPhoneWithPrefix(normalizeKenyanPhone(rawPhone))
                : prev.phone,
        }));
        setErrors((prev) => ({ ...prev, fullName: '', phone: '' }));
    };

    const handleChange = (field, value) => {
        let processedValue = value;

        // Name validation - only allow letters, spaces, hyphens, apostrophes
        if (field === 'fullName') {
            processedValue = value.replace(/[^a-zA-Z\s'-]/g, '');
        }

        // Phone validation - enforce +254 prefix
        if (field === 'phone') {
            processedValue = formatPhoneWithPrefix(value);
        }

        // Postal code validation - only allow alphanumeric
        if (field === 'postalCode') {
            processedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
            // Limit to 10 characters
            if (processedValue.length > 10) {
                processedValue = processedValue.substring(0, 10);
            }
        }

        setForm(prev => ({ ...prev, [field]: processedValue }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        // Full name validation
        if (!form.fullName.trim()) {
            newErrors.fullName = 'Full name is required';
        } else if (!validateName(form.fullName)) {
            newErrors.fullName = 'Name can only contain letters, spaces, hyphens and apostrophes';
        }

        // Phone validation — canonical +2547… / +2541…
        if (!form.phone.trim() || form.phone.trim() === '+254') {
            newErrors.phone = 'Phone number is required';
        } else if (!isValidKenyanPhone(form.phone)) {
            newErrors.phone = KENYA_PHONE_ERROR;
        }

        // Address validation
        if (!form.addressLine1.trim()) {
            newErrors.addressLine1 = 'Address line 1 is required';
        }

        // Location validation
        if (!form.country) newErrors.country = 'Country is required';
        if (!form.state) newErrors.state = 'State/Region is required';
        if (!form.city) newErrors.city = 'City is required';

        // Postal code validation
        if (!form.postalCode.trim()) {
            newErrors.postalCode = 'Postal code is required';
        } else         if (!validatePostalCode(form.postalCode)) {
            newErrors.postalCode = 'Invalid postal code format (3-10 alphanumeric characters)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error('Please fill in all required fields');
            return;
        }
        // Map form fields to match backend expected field names
        const payload = {
            fullName: form.fullName,
            phone: normalizeKenyanPhone(form.phone),
            addressLine1: form.addressLine1,
            addressLine2: form.addressLine2 || undefined,
            countryId: form.country,
            stateId: form.state,
            cityId: form.city,
            postalCode: form.postalCode,
            label: form.label,
        };
        onSubmit(payload);
    };

    const labelOptions = ['home', 'office', 'other'];

    return (
        <div className="bg-card rounded-lg border border-border p-6">
            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {showUseSameAsAbove && (
                        <div className="md:col-span-2 flex justify-end">
                            <button
                                type="button"
                                onClick={applySameAsAbove}
                                disabled={isLoading}
                                className="text-sm text-primary font-medium hover:underline disabled:opacity-50"
                            >
                                Use same as above
                            </button>
                        </div>
                    )}
                    {/* Full Name */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.fullName}
                            onChange={(e) => handleChange('fullName', e.target.value)}
                            placeholder="John Doe"
                            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${errors.fullName ? 'border-red-500' : 'border-border focus:border-primary'
                                }`}
                            disabled={isLoading}
                        />
                        {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
                    </div>

                    {/* Phone */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            placeholder={KENYA_PHONE_PLACEHOLDER}
                            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${errors.phone ? 'border-red-500' : 'border-border focus:border-primary'
                                }`}
                            disabled={isLoading}
                        />
                        {errors.phone ? (
                            <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                        ) : (
                            <p className="text-muted-foreground text-xs mt-1">{KENYA_PHONE_HINT}</p>
                        )}
                    </div>

                    {/* Address Line 1 */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Address Line 1 <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.addressLine1}
                            onChange={(e) => handleChange('addressLine1', e.target.value)}
                            placeholder="Street address, building, apartment"
                            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${errors.addressLine1 ? 'border-red-500' : 'border-border focus:border-primary'
                                }`}
                            disabled={isLoading}
                        />
                        {errors.addressLine1 && <p className="text-red-500 text-xs mt-1">{errors.addressLine1}</p>}
                    </div>

                    {/* Address Line 2 */}
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Address Line 2
                        </label>
                        <input
                            type="text"
                            value={form.addressLine2}
                            onChange={(e) => handleChange('addressLine2', e.target.value)}
                            placeholder="Floor, suite, unit (optional)"
                            className="w-full px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-primary transition-colors"
                            disabled={isLoading}
                        />
                    </div>

                    {/* Country */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Country <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <select
                                value={form.country}
                                onChange={(e) => handleChange('country', e.target.value)}
                                className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none appearance-none bg-card transition-colors ${errors.country ? 'border-red-500' : 'border-border focus:border-primary'
                                    }`}
                                disabled={isLoading || loadingCountries}
                            >
                                <option value="">{loadingCountries ? 'Loading countries...' : 'Select country'}</option>
                                {countries.map(c => (
                                    <option key={c._id} value={c._id}>{c.countryName}</option>
                                ))}
                            </select>
                            <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"></i>
                        </div>
                        {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
                    </div>

                    {/* State */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            State / Region <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <select
                                value={form.state}
                                onChange={(e) => handleChange('state', e.target.value)}
                                className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none appearance-none bg-card transition-colors ${errors.state ? 'border-red-500' : 'border-border focus:border-primary'
                                    }`}
                                disabled={isLoading || loadingStates || !form.country}
                            >
                                <option value="">
                                    {!form.country ? 'Select country first' : loadingStates ? 'Loading states...' : 'Select state/region'}
                                </option>
                                {states.map(s => (
                                    <option key={s._id} value={s._id}>{s.stateName}</option>
                                ))}
                            </select>
                            <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"></i>
                        </div>
                        {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
                    </div>

                    {/* City */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            City <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <select
                                value={form.city}
                                onChange={(e) => handleChange('city', e.target.value)}
                                className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none appearance-none bg-card transition-colors ${errors.city ? 'border-red-500' : 'border-border focus:border-primary'
                                    }`}
                                disabled={isLoading || loadingCities || !form.state}
                            >
                                <option value="">
                                    {!form.state ? 'Select state first' : loadingCities ? 'Loading cities...' : 'Select city'}
                                </option>
                                {cities.map(c => (
                                    <option key={c._id} value={c._id}>{c.cityName}</option>
                                ))}
                            </select>
                            <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"></i>
                        </div>
                        {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
                    </div>

                    {/* Postal Code */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                            Postal Code <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={form.postalCode}
                            onChange={(e) => handleChange('postalCode', e.target.value)}
                            placeholder="00100"
                            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${errors.postalCode ? 'border-red-500' : 'border-border focus:border-primary'
                                }`}
                            disabled={isLoading}
                            maxLength={10}
                        />
                        {errors.postalCode ? (
                            <p className="text-red-500 text-xs mt-1">{errors.postalCode}</p>
                        ) : (
                            <p className="text-muted-foreground text-xs mt-1">3-10 alphanumeric characters</p>
                        )}
                    </div>
                </div>

                {/* Label Selection */}
                <div className="mt-5">
                    <label className="block text-sm font-medium text-foreground mb-2">Address Label</label>
                    <div className="flex items-center gap-4">
                        {labelOptions.map(opt => (
                            <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="addressLabel"
                                    value={opt}
                                    checked={form.label === opt}
                                    onChange={() => handleChange('label', opt)}
                                    className="w-4 h-4 text-primary accent-primary"
                                    disabled={isLoading}
                                />
                                <span className="text-sm text-foreground">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 mt-6 pt-4 border-t border-border">
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="px-6 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isLoading && (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        )}
                        {isLoading ? 'Saving…' : submitLabel}
                    </button>
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isLoading}
                            className="px-6 py-2.5 border border-border text-muted-foreground rounded-lg text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
}
